import ahoy from 'ahoy.js'
import { BookOpen, MessageCircle, RotateCcw, Send, X } from 'lucide-react'
import {
  useCallback,
  useEffect,
  useId,
  useReducer,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'
import Markdown from 'react-markdown'
import { cn } from '@/lib/utils'
import * as api from './api'
import { subscribeToConversation } from './cable'
import { opensInNewTab, safeUrl } from './links'
import type { Message, MountConfig, PageKind } from './protocol'
import { initialState, isBusy, reducer, transcript, type Notice } from './state'

// LF-918: the guidebook chatbot, docked on the right of the page.
//
// Closed, it is an "Ask the Guidebook" chat button in the bottom-right
// corner, where the DigitalOcean widget's was. Open, it is a full-height panel;
// where the window is wide enough for the page beside it (PUSH_AT), the page
// makes room (body.chatbot-push, chatbot.css) rather than sitting under it.

export const PANEL_WIDTH = 380
const MAX_LENGTH = 2000
// The calculator on main needs ~1240px beside its own 364px sidebar.
const PUSH_AT: Record<PageKind, number> = {
  report: 1640,
  home: 1200,
  demo: 1200,
}
const OPEN_BY_DEFAULT: Record<PageKind, boolean> = {
  report: false,
  home: false,
  demo: true,
}
// While a reply is in flight, also check the transcript over HTTP now and
// then, in case the socket is down without saying so.
const POLL_MS = 8000

const REFUSALS: Record<string, Notice> = {
  reply_in_progress: {
    kind: 'limit',
    text: 'Wait for the current answer to finish.',
  },
  conversation_full: {
    kind: 'limit',
    text: 'This chat is full. Start a new chat to keep going.',
  },
  rate_limited: {
    kind: 'limit',
    text: "You've asked a lot of questions this hour. Try again a little later.",
  },
}
const FAILED: Notice = {
  kind: 'error',
  text: "Your question didn't go through. Try again, or start a new chat.",
}

const storageKey = (pageKind: PageKind) => `chatbot.open.${pageKind}`

const readOpen = (pageKind: PageKind): boolean => {
  try {
    const stored = window.localStorage.getItem(storageKey(pageKind))
    return stored === null ? OPEN_BY_DEFAULT[pageKind] : stored === 'true'
  } catch {
    return OPEN_BY_DEFAULT[pageKind]
  }
}

const writeOpen = (pageKind: PageKind, open: boolean) => {
  try {
    window.localStorage.setItem(storageKey(pageKind), String(open))
  } catch {
    // Remembering the panel is a convenience; without storage it just resets.
  }
}

export const ChatbotSidebar = ({ config }: { config: MountConfig }) => {
  const [open, setOpen] = useState(() => readOpen(config.pageKind))
  const [state, dispatch] = useReducer(reducer, undefined, () => initialState())
  const [draft, setDraft] = useState('')
  const [announcement, setAnnouncement] = useState('')
  const stateRef = useRef(state)
  stateRef.current = state
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const scrollRef = useRef<HTMLDivElement>(null)
  const panelRef = useRef<HTMLElement>(null)
  const launcherRef = useRef<HTMLButtonElement>(null)
  // Whether new text should keep the conversation scrolled to the bottom:
  // true until the coach scrolls up to reread something.
  const followRef = useRef(true)
  const wasOpenRef = useRef(open)
  const inputId = useId()
  const busy = isBusy(state)
  const messages = transcript(state)

  const setPanelOpen = useCallback(
    (next: boolean) => {
      setOpen(next)
      writeOpen(config.pageKind, next)
      ahoy.track(next ? 'Chatbot Opened' : 'Chatbot Closed', {
        page: window.location.pathname,
      })
    },
    [config.pageKind],
  )

  // Make room for the panel where the page can spare it.
  useEffect(() => {
    const update = () =>
      document.body.classList.toggle(
        'chatbot-push',
        open && window.innerWidth >= PUSH_AT[config.pageKind],
      )
    update()
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('resize', update)
      document.body.classList.remove('chatbot-push')
    }
  }, [open, config.pageKind])

  useEffect(() => {
    if (open) {
      inputRef.current?.focus()
      followRef.current = true
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
    } else if (wasOpenRef.current) {
      launcherRef.current?.focus()
    }
    wasOpenRef.current = open
  }, [open])

  // Escape closes the panel, but only while focus is inside it, so it never
  // fights the page's own dialogs.
  useEffect(() => {
    if (!open) return
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (
        event.key === 'Escape' &&
        panelRef.current?.contains(document.activeElement)
      )
        setPanelOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, setPanelOpen])

  const lastContent = messages.at(-1)?.content
  useEffect(() => {
    if (followRef.current)
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight })
  }, [messages.length, lastContent])

  const onScroll = () => {
    const el = scrollRef.current
    if (el)
      followRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80
  }

  const resync = useCallback(
    async (generation: number, conversationId: string) => {
      const outcome = await api.fetchTranscript(conversationId)
      if (outcome.ok)
        dispatch({
          type: 'messages',
          generation,
          messages: outcome.value.messages,
        })
    },
    [],
  )

  // Follow the conversation's stream; catch up over HTTP whenever the socket
  // (re)connects, since broadcasts sent while it was down are not replayed.
  const conversationId = state.conversation?.id
  const streamToken = state.conversation?.streamToken
  const generation = state.generation
  useEffect(() => {
    if (!conversationId || !streamToken) return
    return subscribeToConversation(streamToken, {
      onMessage: (message) =>
        dispatch({ type: 'messages', generation, messages: [message] }),
      onConnected: () => {
        dispatch({ type: 'connection', generation, connection: 'connected' })
        void resync(generation, conversationId)
      },
      onDisconnected: () =>
        dispatch({
          type: 'connection',
          generation,
          connection: 'disconnected',
        }),
      onRejected: () =>
        dispatch({
          type: 'connection',
          generation,
          connection: 'disconnected',
        }),
    })
  }, [conversationId, streamToken, generation, resync])

  useEffect(() => {
    if (!conversationId || !busy) return
    const timer = window.setInterval(
      () => void resync(generation, conversationId),
      POLL_MS,
    )
    const onVisible = () => {
      if (document.visibilityState === 'visible')
        void resync(generation, conversationId)
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      window.clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [conversationId, busy, generation, resync])

  // Announce finished answers to screen readers (not every streamed word).
  const lastReply = [...messages].reverse().find((m) => m.role === 'assistant')
  useEffect(() => {
    if (lastReply?.status === 'complete') setAnnouncement('Answer ready.')
    else if (lastReply?.status === 'failed')
      setAnnouncement('The answer failed.')
  }, [lastReply?.id, lastReply?.status])

  const send = async () => {
    const content = draft.trim()
    if (!content || busy || content.length > MAX_LENGTH) return
    const { generation } = stateRef.current
    followRef.current = true
    dispatch({ type: 'sending', generation })

    let conversation = stateRef.current.conversation
    if (!conversation) {
      const started = await api.startConversation(config)
      if (!started.ok) {
        dispatch({ type: 'askFailed', generation, notice: FAILED })
        return
      }
      conversation = started.value
      dispatch({ type: 'conversationStarted', generation, ...started.value })
    }

    const asked = await api.ask(conversation.id, content)
    if (asked.ok) {
      // Only clear what was sent: the coach may have kept typing, or started
      // a new chat, while the question was on its way.
      setDraft((current) =>
        stateRef.current.generation === generation && current.trim() === content
          ? ''
          : current,
      )
      dispatch({ type: 'asked', generation, ...asked.value })
    } else {
      dispatch({
        type: 'askFailed',
        generation,
        notice: REFUSALS[asked.error] ?? FAILED,
      })
    }
  }

  // Puts a question whose answer failed back in the composer, so the coach
  // can send it again without retyping it.
  const askAgain = (question: string | undefined) => {
    if (question) setDraft(question)
    inputRef.current?.focus()
  }

  const newChat = () => {
    if (
      stateRef.current.order.length > 0 &&
      !window.confirm('Start a new chat? This clears the conversation so far.')
    )
      return
    dispatch({ type: 'newChat' })
    setDraft('')
    setAnnouncement('')
    inputRef.current?.focus()
  }

  const onComposerKey = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      !event.nativeEvent.isComposing
    ) {
      event.preventDefault()
      void send()
    }
  }

  if (!open) {
    return (
      <button
        ref={launcherRef}
        type="button"
        onClick={() => setPanelOpen(true)}
        className="fixed bottom-6 right-6 z-[1040] flex h-14 items-center gap-2.5 rounded-full bg-[#031B4E] pl-4 pr-6 text-[16px] font-semibold leading-none text-white shadow-[0_6px_20px_rgba(3,27,78,0.35)] transition hover:-translate-y-0.5 hover:bg-[#0a2a6b] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0061EB] focus-visible:ring-offset-2"
      >
        <MessageCircle className="h-6 w-6 shrink-0" aria-hidden="true" />
        Ask the Guidebook
      </button>
    )
  }

  return (
    <aside
      ref={panelRef}
      aria-label="Guidebook assistant"
      style={{ width: PANEL_WIDTH }}
      className="chatbot-panel fixed right-0 top-0 z-[1040] flex h-screen max-w-full flex-col border-l border-slate-300 bg-white text-[15px] leading-normal text-slate-900 shadow-xl"
    >
      <header className="flex items-center gap-1.5 border-b border-slate-200 bg-[#031B4E] py-3 pl-4 pr-2 text-white">
        <BookOpen className="h-5 w-5 shrink-0" aria-hidden="true" />
        <h2 className="m-0 min-w-0 flex-1 truncate text-[16px] font-semibold leading-tight text-white">
          Guidebook assistant
        </h2>
        <button
          type="button"
          onClick={newChat}
          className="inline-flex items-center gap-1 whitespace-nowrap rounded-md px-2 py-1 text-[14px] text-white hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          New chat
        </button>
        <button
          type="button"
          onClick={() => setPanelOpen(false)}
          className="rounded-md p-1 text-white hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
          aria-label="Close the guidebook assistant"
        >
          <X className="h-5 w-5" aria-hidden="true" />
        </button>
      </header>

      <div
        ref={scrollRef}
        onScroll={onScroll}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
      >
        <div className="rounded-lg bg-slate-100 px-3 py-2">
          <p className="m-0 whitespace-pre-line">{config.greeting}</p>
          <p className="m-0 mt-2 text-[13px] text-slate-600">
            Answers come from Leap Fund&apos;s Guidebook.
          </p>
        </div>
        {messages.map((message) => (
          <ChatMessage
            key={message.id}
            message={message}
            onAskAgain={
              message.replyToId
                ? () =>
                    askAgain(state.messages[message.replyToId ?? '']?.content)
                : undefined
            }
          />
        ))}
        {state.notice && (
          <div
            role="alert"
            className={cn(
              'rounded-md px-3 py-2 text-[13px]',
              state.notice.kind === 'error'
                ? 'bg-red-50 text-red-800'
                : 'bg-amber-50 text-amber-900',
            )}
          >
            {state.notice.text}
            {state.notice.kind === 'limit' && <NewChatLink onClick={newChat} />}
          </div>
        )}
      </div>

      <form
        className="border-t border-slate-200 px-4 py-3"
        onSubmit={(event) => {
          event.preventDefault()
          void send()
        }}
      >
        {state.connection === 'disconnected' && (
          <p className="m-0 mb-2 text-[12px] text-slate-500">Reconnecting…</p>
        )}
        <label htmlFor={inputId} className="sr-only">
          Ask a question about the guidebook or the calculator
        </label>
        <div className="flex items-end gap-2">
          <textarea
            id={inputId}
            ref={inputRef}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onComposerKey}
            rows={2}
            maxLength={MAX_LENGTH}
            placeholder="Ask about the guidebook or the calculator…"
            className="max-h-40 min-h-[2.75rem] flex-1 resize-y rounded-md border border-slate-300 px-3 py-2 text-[15px] focus:border-[#0061EB] focus:outline-none focus:ring-1 focus:ring-[#0061EB]"
          />
          <button
            type="submit"
            disabled={busy || draft.trim().length === 0}
            className="inline-flex h-10 w-10 items-center justify-center rounded-md bg-[#0061EB] text-white hover:bg-[#004fc0] disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Send"
          >
            <Send className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
        {draft.length > MAX_LENGTH - 200 && (
          <p className="m-0 mt-1 text-right text-[12px] text-slate-500">
            {draft.length}/{MAX_LENGTH}
          </p>
        )}
        <p className="sr-only" aria-live="polite">
          {announcement}
        </p>
      </form>
    </aside>
  )
}

const NewChatLink = ({ onClick }: { onClick: () => void }) => (
  <>
    {' '}
    <button
      type="button"
      onClick={onClick}
      className="font-medium text-[#0061EB] underline underline-offset-2"
    >
      Start a new chat
    </button>
  </>
)

const ChatMessage = ({
  message,
  onAskAgain,
}: {
  message: Message
  onAskAgain?: () => void
}) => {
  if (message.role === 'user') {
    return (
      <div className="ml-8 whitespace-pre-wrap rounded-lg bg-[#E5E8ED] px-3 py-2">
        {message.content}
      </div>
    )
  }

  const waiting =
    message.status === 'pending' ||
    (message.status === 'streaming' && !message.content)
  return (
    <div className="mr-4">
      {waiting ? (
        <p className="m-0 animate-pulse text-slate-500">
          Looking in the guidebook…
        </p>
      ) : (
        <div className="chatbot-answer">
          <Markdown
            urlTransform={safeUrl}
            disallowedElements={['img']}
            unwrapDisallowed
            components={{
              a: ({ href, children }) =>
                href ? (
                  <a
                    href={href}
                    onClick={() =>
                      ahoy.track('Chatbot Link Clicked', {
                        url: href,
                        page: window.location.pathname,
                      })
                    }
                    {...(opensInNewTab(href)
                      ? { target: '_blank', rel: 'noopener noreferrer' }
                      : {})}
                  >
                    {children}
                  </a>
                ) : (
                  <>{children}</>
                ),
            }}
          >
            {message.content}
          </Markdown>
        </div>
      )}
      {message.truncated && (
        <p className="m-0 mt-1 text-[12px] text-slate-500">
          This answer was cut short. Ask a narrower question for the rest.
        </p>
      )}
      {message.status === 'failed' && (
        <p className="m-0 mt-1 text-[13px] text-red-800">
          {message.error}{' '}
          {onAskAgain ? (
            <button
              type="button"
              onClick={onAskAgain}
              className="font-medium text-[#0061EB] underline underline-offset-2"
            >
              Ask again
            </button>
          ) : (
            'Try asking again'
          )}
          , or start a new chat.
        </p>
      )}
    </div>
  )
}
